import { api, apiErrorMessage, oneSpec, useAuthStore } from '@bemor/web-kit';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    DetailRow,
    Field,
    FieldGrid,
    Input,
    SearchableSelect,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type {
    MerchantPricing,
    PricingAssignment,
    PricingCardSummary,
    ReservePolicy,
} from '@/api/types';
import { P } from '@/lib/permissions';
import { useList, useOne, useWrite } from '@/lib/queries';
import { useMe, who } from '@/lib/staff';
import { CardLabelBadge, NewCardDialog } from '@/pages/pricing/cards';
import { reserveText } from '@/pages/pricing/reserve-policies';

/** The merchant's pricing read, run by the merchant page and its prefetch. */
export const merchantPricingSpec = (id: string | undefined) =>
    oneSpec(`/staff/merchants/${id ?? ''}/pricing`);

/** The merchant's pricing, read at the page level for staff who may read it. */
export function useMerchantPricing(merchantId: string | undefined) {
    const can = useAuthStore((s) => s.can);
    return useOne<MerchantPricing>(
        can(P.pricingRead) && merchantId
            ? merchantPricingSpec(merchantId).path
            : null,
    );
}

type CardRef = PricingAssignment['fee_card'];

function CardLink({ card }: { card: CardRef }) {
    if (!card) return <span className="text-fg-2">None</span>;
    return (
        <span className="flex flex-wrap items-center gap-2">
            <Link
                to={`/pricing/cards/${card.id}`}
                className="font-medium hover:underline"
            >
                {card.name}
            </Link>
            <span className="text-fg-2 text-xs">
                v{card.version}
                {card.merchant_id ? ' · negotiated' : ' · tenant default'}
            </span>
            <CardLabelBadge label={card.label} />
        </span>
    );
}

const cardOptions = (rows: readonly PricingCardSummary[], merchantId: string) =>
    rows
        .filter((c) => c.merchant_id === null || c.merchant_id === merchantId)
        .map((c) => ({
            value: c.id,
            label: `${c.name} (v${c.version}, ${c.merchant_id ? 'negotiated' : 'tenant default'})`,
        }));

function AssignForm({
    merchantId,
    current,
    onDone,
}: {
    merchantId: string;
    current: PricingAssignment | null;
    onDone: () => void;
}) {
    const fees = useList<PricingCardSummary>('/staff/cards', {
        side: ['fee'],
        per_page: 100,
    });
    const costs = useList<PricingCardSummary>('/staff/cards', {
        side: ['cost'],
        per_page: 100,
    });
    const policies = useList<ReservePolicy>('/staff/reserve-policies', {
        per_page: 100,
    });
    const [fee, setFee] = useState<string | null>(
        current?.fee_card?.id ?? null,
    );
    const [cost, setCost] = useState<string | null>(
        current?.cost_card?.id ?? null,
    );
    const [policy, setPolicy] = useState<string | null>(
        current?.reserve_policy?.id ?? null,
    );
    const [reason, setReason] = useState('');
    const [error, setError] = useState<string | null>(null);
    const assign = useWrite(
        async () =>
            (
                await api().post<PricingAssignment>(
                    `/staff/merchants/${merchantId}/pricing`,
                    {
                        fee_card_id: fee,
                        cost_card_id: cost,
                        reserve_policy_id: policy,
                        reason,
                    },
                )
            ).data,
        [`/staff/merchants/${merchantId}/pricing`, '/staff/cards'],
    );
    return (
        <form
            method="post"
            className="space-y-4"
            data-testid="assign-pricing"
            onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                assign.mutate(undefined, {
                    onSuccess: onDone,
                    onError: (err) => setError(apiErrorMessage(err)),
                });
            }}
        >
            {error ? (
                <p role="alert" className="text-destructive text-sm">
                    {error}
                </p>
            ) : null}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Field id="assign-fee" label="Fee card">
                    <SearchableSelect
                        id="assign-fee"
                        label="Fee card"
                        value={fee}
                        options={cardOptions(
                            fees.data?.data.items ?? [],
                            merchantId,
                        )}
                        onChange={setFee}
                    />
                </Field>
                <Field
                    id="assign-cost"
                    label="Cost card"
                    hint="Empty: the tenant's cost card"
                >
                    <SearchableSelect
                        id="assign-cost"
                        label="Cost card"
                        value={cost}
                        clearable
                        options={cardOptions(
                            costs.data?.data.items ?? [],
                            merchantId,
                        )}
                        onChange={setCost}
                    />
                </Field>
                <Field id="assign-policy" label="Reserve policy">
                    <SearchableSelect
                        id="assign-policy"
                        label="Reserve policy"
                        value={policy}
                        options={(policies.data?.data.items ?? [])
                            .filter(
                                (p) =>
                                    p.merchant_id === null ||
                                    p.merchant_id === merchantId,
                            )
                            .map((p) => ({
                                value: p.id,
                                label: `${p.name} (${reserveText(p)})`,
                            }))}
                        onChange={setPolicy}
                    />
                </Field>
            </div>
            <Field id="assign-reason" label="Reason">
                <Input
                    id="assign-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                />
            </Field>
            <div className="flex flex-wrap gap-2">
                <Button
                    type="submit"
                    size="sm"
                    disabled={
                        !fee ||
                        !policy ||
                        reason.trim() === '' ||
                        assign.isPending
                    }
                >
                    Assign
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={onDone}
                >
                    Cancel
                </Button>
            </div>
        </form>
    );
}

/**
 * BM-62 2.6: the merchant's pricing (replacing the fee plan): the fee card, cost card and reserve policy in force,
 * the assignment history, and the assignment of new ones with a reason. Treasury pricing lives in Ledger (BM-65).
 */
export function MerchantPricingCard({
    merchantId,
    query,
}: {
    merchantId: string;
    /** The page's read (`useMerchantPricing`), so the route's prefetch finds it before the merchant loads. */
    query: ReturnType<typeof useMerchantPricing>;
}) {
    const me = useMe();
    const can = useAuthStore((s) => s.can);
    const [assigning, setAssigning] = useState(false);
    const [newCard, setNewCard] = useState(false);
    if (!can(P.pricingRead)) return null;
    const pricing = query.data?.data;
    const current = pricing?.current ?? null;
    return (
        <Card data-testid="merchant-pricing">
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-sm font-medium">Pricing</CardTitle>
                <div className="flex flex-wrap gap-2">
                    {can(P.pricingManage) ? (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setNewCard(true)}
                        >
                            New negotiated card
                        </Button>
                    ) : null}
                    {can(P.pricingAssign) && pricing && !assigning ? (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setAssigning(true)}
                        >
                            Change pricing
                        </Button>
                    ) : null}
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {query.isError ? (
                    <p role="alert" className="text-destructive text-sm">
                        {apiErrorMessage(query.error)}
                    </p>
                ) : !pricing ? (
                    <p className="text-fg-2 text-sm">Loading…</p>
                ) : assigning ? (
                    <AssignForm
                        merchantId={merchantId}
                        current={current}
                        onDone={() => setAssigning(false)}
                    />
                ) : current ? (
                    <FieldGrid>
                        <DetailRow
                            label="Fee card"
                            value={<CardLink card={current.fee_card} />}
                            layout="stacked"
                        />
                        <DetailRow
                            label="Cost card"
                            value={
                                current.cost_card ? (
                                    <CardLink card={current.cost_card} />
                                ) : (
                                    "The tenant's cost card"
                                )
                            }
                            layout="stacked"
                        />
                        <DetailRow
                            label="Reserve"
                            value={
                                current.reserve_policy
                                    ? `${current.reserve_policy.name ?? ''} (${reserveText(current.reserve_policy)})`
                                    : 'None'
                            }
                            layout="stacked"
                        />
                        <DetailRow
                            label="Since"
                            value={
                                <span>
                                    <DateTime
                                        zone="inline"
                                        value={current.active_from}
                                        empty="—"
                                    />{' '}
                                    · {who(current.set_by, me)}
                                    {current.reason
                                        ? ` · ${current.reason}`
                                        : ''}
                                </span>
                            }
                            layout="stacked"
                        />
                    </FieldGrid>
                ) : (
                    <p className="text-fg-2 text-sm">
                        No pricing assigned: the merchant cannot take payments
                        until it has a fee card.
                    </p>
                )}
                {pricing && pricing.history.length > 0 ? (
                    <div>
                        <p className="text-fg-2 mb-1 text-xs">History</p>
                        <ul
                            className="divide-y text-sm"
                            data-testid="pricing-history"
                        >
                            {pricing.history.map((a, i) => (
                                <li
                                    key={i}
                                    className="flex flex-wrap gap-x-2 py-1"
                                >
                                    <span>
                                        {a.fee_card?.name ?? '—'} v
                                        {a.fee_card?.version}
                                    </span>
                                    <span className="text-fg-2">
                                        <DateTime
                                            value={a.active_from}
                                            empty="—"
                                        />
                                        {a.active_to ? (
                                            <>
                                                {' '}
                                                to{' '}
                                                <DateTime
                                                    value={a.active_to}
                                                    empty="—"
                                                />
                                            </>
                                        ) : null}{' '}
                                        · {who(a.set_by, me)}
                                        {a.reason ? ` · ${a.reason}` : ''}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : null}
            </CardContent>
            <NewCardDialog
                open={newCard}
                onOpenChange={setNewCard}
                merchantId={merchantId}
            />
        </Card>
    );
}
